import { Menu, ClickAwayListener } from '@material-ui/core';
import JSZip from 'jszip';
import saveAs from 'file-saver';
import { MyMenuItem } from '../../styling/Styles';
import { React, useContext, clsx, api } from '../../barrelFiles/basicBarrelFile';
import AuthContext from '../AppAuthContext';
import {
  convertBodyToHtmlCode,
  recreatePageTransitionCommand,
  createHrefTransitionCommand,
  convertIdToName,
  changeMethodName,
  addFunctionToJavascript,
  createFileEventListener,
  createDatepickerMethod,
  createBackHistoryCommand,
  createServiceFunction,
  findService
} from '../BuildApps/GrapesJs/LoadCommands/ExportCode';
import apiService from '../../services/apiService';
import ExportProjectCodeViolationsPopUp from '../GeneralComponents/ExportProjectCodeViolationsPopUp';

/**
 * 
 * @param { document component} comp - Document component  
 * @param {int} indexPage -  index of examined page
 * @param {[]} functionEntities - array with objects for every entity and their functionality
 * @returns array with commands that have been attached to specific component
 */
const createJavascriptPerComp = async (comp, indexPage, functionEntities) => {
  let tempMethodName = 'none'; // initialize examined method name
  let tempAnnotationName = 'none'; // initialize examined annotation name
  let tempEntity = ''; // initialize examined entity
  const commands = []; // initialize commands
  const compId = comp.getAttribute('id'); // component's id 
  const annotationName = comp.getAttribute('annotationname'); // component's annotationname 
  const targetPage = comp.getAttribute('target_page'); // component's target page
  const type = comp.getAttribute('type'); // component's type
  const href = comp.getAttribute('href'); // component's href 
  const openWindow = comp.hasAttribute('open-window'); // component's open window attribute
  const transitionById = comp.hasAttribute('transition-by-id'); // component's transition by id attribute
  const arrayInput = comp.getAttribute('arrayinput'); // component's array input attribute
  const backButton = comp.getAttribute('back-button'); // component's back button attribute
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject')); // number which shows index of user's selected project

  // Check if component has been attached with a navigate to another page command
  if (targetPage !== null) {

    const existsOpenWindow = openWindow === true; // Check if navigate to another page command will open to a new window
    const existsTransitionById = transitionById === true; // Check if navigate to another page command will send id as parameter
    // creating navigate to another page command
    commands.push(
      recreatePageTransitionCommand(
        convertIdToName(targetPage),
        compId,
        existsOpenWindow,
        existsTransitionById
      )
    );
  }

  // Check if component has been attached to navigate to previous page command
  if( backButton !== null) {
    // Creating navigate to previous page command
    commands.push(
      createBackHistoryCommand(compId)
    )
  }


  // Check if component has been attached with creating array input command
  if (arrayInput !== null && arrayInput === 'true') {
    // Creating array input command
    commands.push({
      functionName: 'InputArray',
      functionality: '',
      compIds: [compId],
      inputDataTemplate: '',
      methodType: 'ComplexInput'
    });
  }

  //  The advance-select flag for some reason it is simply stated without value
  if (comp.hasAttribute('advance-select')) {
    commands.push({
      functionName: 'alignSelectOptionEvent',
      functionality: '',
      compIds: [compId],
      inputDataTemplate: '',
      methodType: 'SelectAlignEvent'
    });
  }

  // Check if component is of type file
  if (type !== null && type === 'file') {
    // Create file event listener command
    commands.push(createFileEventListener(compId));
  }

  // Check if component is of type dateinput
  if (compId !== null && String(compId).startsWith('datepicker') === true) {
    // Create date picker command
    commands.push(createDatepickerMethod(compId));
  }

  // Check if component has been attached to navigate to external link command
  if (href !== null && href !== '#' && href !== '#!') {
      // Creating navigate to external link command
      commands.push(createHrefTransitionCommand(href, compId, openWindow === true)) 
  }

  // Check if command has been attached with annotation name attribute
  if(annotationName !== null) {
    // Check if component has been attached with a service command
    if(findService(annotationName) === true){
      // Creating service function command
      commands.push(createServiceFunction(annotationName, compId));
      return commands;
    }
  }

  // Check if component has annotation name attribute 
  if (annotationName !== null) {
    const { annotations } = JSON.parse(localStorage.getItem('data'))[selectedProject]; // Find existing annotations
    let foundMethod = false;
    if (annotations[0] !== undefined) {
      annotations[0].combinations.forEach((comb) => {
        if (comb.annotationName === annotationName && comb.method !== 'none') {
          tempMethodName = comb.method; // find name of annotated method that has been attached to component
          tempAnnotationName = comb.annotationName;
          foundMethod = true; 
          tempEntity = comb.annotationLink; // find entity that annotationname belongs
        }
      });
    }
    // Check if component has attached annotated method
    if (foundMethod === true && tempMethodName !== 'none' && tempEntity !== '') {
      const pageName = JSON.parse(localStorage.getItem('data'))[selectedProject].pages[indexPage].name; 
      // initialize command
      let command = { functionName: '', functionality: '', compIds: [compId] };
      if (
        tempMethodName !== 'none' &&
        functionEntities !== undefined &&
        functionEntities[tempEntity] !== undefined &&
        functionEntities[tempEntity][tempMethodName] !== undefined 
      ) {
        command.functionName = tempMethodName;
        command.entityName = tempEntity;
        command.annotationName = tempAnnotationName;
        command = { ...command, ...functionEntities[tempEntity][tempMethodName], pageName };
      }
      commands.push(command);
    } 
  }
  return commands;
};

/**
 * Array function that parses css of all pages of selected project and export them 
 * in a zip file that contains all .css files for every page
 */
const exportCssProject = () => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
  const tempData = JSON.parse(localStorage.getItem('data'))[selectedProject];
  const zip = new JSZip();

  const pages = zip.folder('css'); // Create folder with name css

  // Parse css content of all pages
  tempData.pages.forEach((content) => {
    // Create for every page a file with name same as page name and content the css of the page 
    if (content.css !== undefined) {
      pages.file(`${content.name}.css`, content.css);
    } else {
      pages.file(`${content.name}.css`, '');
    }
  });

  // Create a zip file with name CSSContent.zip and put inside the 'css' folder 
  zip.generateAsync({ type: 'blob' }).then((content) => {
    saveAs(content, 'CSSContent.zip');
  });
};

/**
 * Array function that parses html of all pages of selected project and export them 
 * in a zip file that contains all .html files for every page
 */
const exportHtmlProject = () => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
  const tempData = JSON.parse(localStorage.getItem('data'))[selectedProject];
  const zip = new JSZip();

  const pages = zip.folder('html'); // Create folder with name html

  // Parse html content of all pages
  tempData.pages.forEach((content) => {
    // Create for every page a file with name same as page name and content the html of the page 
    if (content.html !== undefined) {
      pages.file(`${content.name}.html`, content.html);
    } else {
      pages.file(`${content.name}.html`, convertBodyToHtmlCode('', content.name));
    }
  });
  // Create a zip file with name HTMLContent.zip and put inside the 'html' folder 
  zip.generateAsync({ type: 'blob' }).then((content) => {
    saveAs(content, 'HTMLContent.zip');
  });
};

/**
 * Array function that parses css and html of all pages of selected project and export them 
 * in a zip file that contains all .css and .html files for every page
 */
const exportCSSHTMLProject = () => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));

  const tempData = JSON.parse(localStorage.getItem('data'))[selectedProject];
  const zip = new JSZip();

  const pages1 = zip.folder('html'); // Create folder with name html
  // Parse html content of all pages
  tempData.pages.forEach((content) => {
    // Create for every page a file with name same as page name and content the html of the page 
    if (content.html !== undefined) {
      pages1.file(`${content.name}.html`, content.html);
    } else {
      pages1.file(`${content.name}.html`, convertBodyToHtmlCode('', content.name));
    }
  });

  const pages2 = zip.folder('css'); // Create folder with name css
  // Parse css content of all pages
  tempData.pages.forEach((content) => {
    // Create for every page a file with name same as page name and content the css of the page 
    if (content.css !== undefined) {
      pages2.file(`${content.name}.css`, content.css);
    } else {
      pages2.file(`${content.name}.css`, '');
    }
  });

  // Create a zip file with name CSS&HTMLContent.zip and put inside the 'html' and 'css' folder 
  zip.generateAsync({ type: 'blob' }).then((content) => {
    saveAs(content, 'CSS&HTMLContent.zip');
  });
};

/**
 * @param {setState} setDisplayExportBackendSpinner - Hook that updates the spinner that is running when server is downloading
 * @param {funcion} dispatch - it is called to update the state  
 * @param {setState} setDisplayWentWrongUi - Hook that updates the display of midlayer with general error message
 * @param {setState} setViolationMessage - Hook that updates the violation message that appears on error pop up
 * @param {setState} setOpenExportProjectCodePopUp - Hook that updates the display of error pop up
 * This function is downloading as zip the server that WABLI is producing for a project
 */
const requestCodeServer = (
  setDisplayExportBackendSpinner,
  dispatch,
  setDisplayWentWrongUi,
  setViolationMessage,
  setOpenExportProjectCodePopUp
) => {
  api
    .getServerDLLink(
      JSON.parse(localStorage.getItem('data'))[localStorage.getItem('selectedProject')]
    )
    // Download as zip produced server for specific project
    .then((downloadLink) => api.downloadUrlSaveAsZip(downloadLink.downloadLink, dispatch))
    .then(() => {
      // Update spinner to hiden when server has been downloaded
      setDisplayExportBackendSpinner(false);
    })
    .catch((error) => {
      // Update spinner to hiden when error has been caught
      setDisplayExportBackendSpinner(false);
      // Open pop up and display specific violation message based on error that has been caught
      if (error.response.data === 'empty pages-data-model') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty page data model');
      } else if (error.response.data === 'empty deploy-configuration') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty deploy configuration');
      } else if (error.response.data === 'empty project-pages') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty project pages');
      } else {
        // If specific error is not caught then a mid layer with a general message is appeared
        setDisplayWentWrongUi(true);
      }
      throw error.response
    });
};

/** 
 * @param {setState} setDisplayExportBackendSpinner - Hook that updates the spinner that is running when server is downloading
 * @param {funcion} dispatch - it is called to update the state  
 * @param {setState} setDisplayWentWrongUi - Hook that updates the display of midlayer with general error message
 * @param {setState} setViolationMessage - Hook that updates the violation message that appears on error pop up
 * @param {setState} setOpenExportProjectCodePopUp - Hook that updates the display of error pop up
 * This function is downloading as zip the frontSdk that WABLI is producing for a project
 */
const requestFrontSdk = (
  setDisplayExportFrontSdkSpinner,
  dispatch,
  setDisplayWentWrongUi,
  setViolationMessage,
  setOpenExportProjectCodePopUp
) => {
  api
    .getFrontsdkDLLink(
      JSON.parse(localStorage.getItem('data'))[localStorage.getItem('selectedProject')],
      dispatch
    )
    // Download as zip produced frontSdk for specific project
    .then((downloadLink) => {
      api.downloadUrlSaveAsZip(downloadLink.downloadLink, dispatch);
    })
    .then(() => setDisplayExportFrontSdkSpinner(false))
    .catch((error) => {
      setDisplayExportFrontSdkSpinner(false);
      // Open pop up and display specific violation message based on error that has been caught
      if (error.response.data === 'empty pages-data-model') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty page data model');
      } else if (error.response.data === 'empty deploy-configuration') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty deploy configuration');
      } else if (error.response.data === 'empty project-pages') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty project pages');
      } else {
        // If specific error is not caught then a mid layer with a general message is appeared
        setDisplayWentWrongUi(true);
      }
      throw error;
    });
};


/** 
 * @param {setState} setDisplayExportBackendSpinner - Hook that updates the spinner that is running when server is downloading
 * @param {funcion} dispatch - it is called to update the state  
 * @param {setState} setDisplayWentWrongUi - Hook that updates the display of midlayer with general error message
 * @param {setState} setViolationMessage - Hook that updates the violation message that appears on error pop up
 * @param {setState} setOpenExportProjectCodePopUp - Hook that updates the display of error pop up
 * @param {string} reason - Reason that function is called. May be ExportFrontSdk, SyncToGithub or AnalyzeProject
 * This function is syncing both produced frontSdk and server to Github
 */
const syncToGithub = (
  setDisplaySyncToGithubSpinner,
  dispatch,
  setDisplayWentWrongUi,
  setViolationMessage,
  setOpenExportProjectCodePopUp,
  reason
) => {
  // Download project's frontSdk link
  api
    .getFrontsdkDLLink(
      JSON.parse(localStorage.getItem('data'))[localStorage.getItem('selectedProject')],
      dispatch
    )
    // Download project's server link
    .then((downloadLink) => {
      api
        .getServerDLLink(
          JSON.parse(localStorage.getItem('data'))[localStorage.getItem('selectedProject')]
        )
        // Syncing frontSdk to Github
        .then(() => {
          api.syncToGithub(
            `${downloadLink.downloadLink.split('download')[0]}syncToGithub`,
            downloadLink,
            'frontEnd',
            dispatch
          );
        })
        // Syncing server to Github
        .then(() => {
          api.syncToGithub(
            `${downloadLink.downloadLink.split('download')[0]}syncToGithub`,
            downloadLink,
            'server',
            dispatch
          );
          setDisplaySyncToGithubSpinner(false); // Set sync to github spinner to hidden
        })
        .catch((error) => {
          setDisplaySyncToGithubSpinner(false); // Set sync to github spinner to hidden
          setDisplayWentWrongUi(true); // Set mid layer with general error message to visible
          throw error.response;
        });
    })
    .catch((error) => {
      // If reason is AnalyzeProject set display sync to Github spinner to false
      if (reason !== 'AnalyzeProject') {
        setDisplaySyncToGithubSpinner(false);
      }
      // Open pop up and display specific violation message based on error that has been caught
      if (error.response.data === 'empty pages-data-model') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty page data model');
      } else if (error.response.data === 'empty deploy-configuration') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty deploy configuration');
      } else if (error.response.data === 'empty project-pages') {
        setOpenExportProjectCodePopUp(true);
        setViolationMessage('Empty project pages');
      } else {
        // If specific error is not caught then a mid layer with a general message is appeared
        setDisplayWentWrongUi(true);
      }
      throw error;
    });
};

/**
 * 
 * @param {setState} spinner - handles state of spinner that is running while creating javascript for every page
 * @param {funcion} dispatch - it is called to update the state  
 * @param {setState} setDisplayWentWrongUi - Hook that updates the display of midlayer with general error message
 * @param {string} reason - Reason that function is called. May be ExportFrontSdk, SyncToGithub or AnalyzeProject
 * @param {setState} setViolationMessage - Hook that updates the violation message that appears on error pop up
 * @param {setState} setOpenExportProjectCodePopUp - Hook that updates the display of error pop up
 * @returns 
 */
const createJavascriptPerPage = async (
  spinner,
  dispatch,
  setDisplayWentWrongUi,
  reason,
  setViolationMessage,
  setOpenExportProjectCodePopUp
) => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
  const tempData = JSON.parse(localStorage.getItem('data'));
  const pagesLength = JSON.parse(localStorage.getItem('data'))[selectedProject].pages.length;
  let entitieNames = JSON.parse(localStorage.getItem('data'))[selectedProject].annotations[0] !== undefined ? JSON.parse(localStorage.getItem('data'))[selectedProject].annotations[0].entities : [];
  const projectId = JSON.parse(localStorage.getItem('data'))[selectedProject]._id;
  let dataElements = [];
  dataElements = JSON.parse(localStorage.getItem('data'))[selectedProject].pages.map((page) => page.data)
      .reduce((acc, curVal) =>  acc.concat(curVal), [])
      .reduce((uniqueData, data) => {
        if (uniqueData && data !== undefined) {

          if(uniqueData.get(data.dataName) !== undefined){
            const newSchema = data.schema.concat(uniqueData.get(data.dataName).schema);
            const entryNames = newSchema.map( o => o.entryName);
            const filteredNewSchema = newSchema.filter( ({entryName},index) => !entryNames.includes(entryName,index+1))

            const newActions = data.actions.concat(uniqueData.get(data.dataName).actions);
            const actionNames = newActions.map( o => o.actionName);
            const filterNewActions = newActions.filter( ({actionName},index) => !actionNames.includes(actionName,index +1));

            uniqueData.set(data.dataName, {
              ...uniqueData.get(data.dataName),
              schema: filteredNewSchema,
              actions: filterNewActions
            });
          }
          else{
            uniqueData.set(data.dataName, {
              attributeName: data.attributeName,
              schema: data.schema,
              actions: data.actions
            });
          }
        } else if( data !== undefined) {
          uniqueData.set(data.dataName, {
            ...uniqueData.get(data.dataName),
            schema: data.schema.concat(uniqueData.get(data.dataName).schema),
            actions: data.actions.concat(uniqueData.get(data.dataName).actions)
          });
        }
        return uniqueData;
      }, new Map());
    
    entitieNames = Array.from(dataElements.values()).map( data => data.attributeName);
    

  // Create array with objects with main javascript functiionality for every entity inside the project
  return Promise.all(
    entitieNames
      .map(async(entity) => {
        const functionEntity = await apiService
        .getFrontSDKArtifact(projectId, {
          fileName: `${entity[0].toUpperCase() + entity.slice(1)}Api.json`
        })
        .then((response) => changeMethodName(response.data, entity))
        .then((renamedMethods) => renamedMethods)
        .catch((err) => {throw err});
        return { 'entity' : entity, 'func': functionEntity};
      })
    )
    .then( functionEntities => {
      const funcObj = {}
      functionEntities.forEach( func => {
        funcObj[func.entity] = func.func;
      })
      return funcObj;
    })
    // Create javascript file for every page
    .then( async functionEntities => 
      Promise.all(
        Array(pagesLength)
          .fill(1)
          .map(async (comp, indexPage) => {
            // Create document based on html that is stored in localStorage for every page
            const doc = new DOMParser().parseFromString(
              tempData[selectedProject].pages[indexPage].html,
              'text/html'
            );
            const elements = doc.querySelectorAll('[id]');
    
            // Iterate throw all elements with attribute id and return javascript file
            const response = await Promise.all(
              Array(elements.length)
                .fill(1)
                // Create array with objects. Every object contains commands that have been attached to a component
                .map(async (component, elementIndex) => createJavascriptPerComp(elements[elementIndex], indexPage, functionEntities)))
                // Based on attached commands for every component produce javascript file
                .then((responses) => addFunctionToJavascript(responses, indexPage))
                .then(() => 1)
                .catch((err) => {
                  spinner(false); // Set state of spinner to false
                  throw err;
                });
    
            return response;
          })
      )
        .then(async () => {
          const response = await Promise.all(
            Array(pagesLength)
              .fill(1)
              .map(async (comp, pageIndex) => {
                const tempPage = JSON.parse(localStorage.getItem('data'))[selectedProject].pages[
                  pageIndex
                ];
                // Store javascript file for every page
                const res = await apiService.updatePage(tempPage);
                return res;
              })
          );
          return response;
        })
        .then(() => {
          // Request front sdk
          if (reason === 'ExportFrontSdk') {
            requestFrontSdk(
              spinner,
              dispatch,
              setDisplayWentWrongUi,
              setViolationMessage,
              setOpenExportProjectCodePopUp
            );
          }
          // Sync to Github  
          else if (reason === 'SyncToGithub' || reason === 'AnalyzeProject') {
            syncToGithub(
              spinner,
              dispatch,
              setDisplayWentWrongUi,
              setViolationMessage,
              setOpenExportProjectCodePopUp,
              reason
            );
          }
        })
        .catch((error) => {
          // Hide spinner if error has been caught
          if (reason === 'ExportFrontSdk') {
            spinner(false);
          } else if (reason === 'SyncToGithub') {
            spinner(false);
          }
          // Display general mid layer if error has been caught
          setDisplayWentWrongUi(true);
          throw error;
        })
    )
};


/**
 * 
 * @param {setState} props.setDisplayExportBackendSpinner -
 * @param {setState} props.setDisplayExportFrontSdkSpinner - 
 * @param {setState} props.setDisplayWentWrongUi -
 * @param {setState} props.setDisplaySyncToGithubSpinner -
 * @param {boolean} props.enableBreadCrumb - 
 * @returns dropdown menu with export option
 */
const ExportProjectCode = (props) => {
  const {
    setDisplayExportBackendSpinner,
    setDisplayExportFrontSdkSpinner,
    setDisplayWentWrongUi,
    setDisplaySyncToGithubSpinner,
    enableBreadCrumb
  } = props;

  const downloadOptions = [
    'Export project CSS',
    'Export project HTML',
    'Export project CSS & HTML',
    'Export backend',
    'Export front sdk',
    'Sync to github'
  ];
  const [anchorEl2, setAnchorEl2] = React.useState(); // State of anchor element to open dropdown menu
  const [stateDownloadptions, setStateDownloadOptions] = React.useState(false); // State of dropdown menu 
  const { dispatch } = useContext(AuthContext); // Method to update React state 
  const [openExportProjectCodePopUp, setOpenExportProjectCodePopUp] = React.useState(false); // State to open/close display export code violations pop up
  const [violationMessage, setViolationMessage] = React.useState(''); // Violation message inside export code violations pop up

  // Open export options menu 
  const openDisplayExportOptions = (event) => {
    setAnchorEl2(event.currentTarget);
    setStateDownloadOptions(true);
  };

  // Close export options menu 
  const closeDisplayExportOptions = () => {
    setStateDownloadOptions(false);
    setAnchorEl2(null);
  };

  // Select option in export menu
  const selectExportOption = (index) => {
    if (index === 0) {
      exportCssProject();
    } else if (index === 1) {
      exportHtmlProject();
    } else if (index === 2) {
      exportCSSHTMLProject();
    } else if (index === 3) {
      setDisplayExportBackendSpinner(true);
      requestCodeServer(
        setDisplayExportBackendSpinner,
        dispatch,
        setDisplayWentWrongUi,
        setViolationMessage,
        setOpenExportProjectCodePopUp
      );
    } else if (index === 4) {
      setDisplayExportFrontSdkSpinner(true);
      const reason = 'ExportFrontSdk';
      const spinner = setDisplayExportFrontSdkSpinner;
      createJavascriptPerPage(
        spinner,
        dispatch,
        setDisplayWentWrongUi,
        reason,
        setViolationMessage,
        setOpenExportProjectCodePopUp
      );
    } else if (index === 5) {
      const reason = 'SyncToGithub';
      const spinner = setDisplaySyncToGithubSpinner;
      setDisplaySyncToGithubSpinner(true);
      createJavascriptPerPage(
        spinner,
        dispatch,
        setDisplayWentWrongUi,
        reason,
        setViolationMessage,
        setOpenExportProjectCodePopUp
      );
    }
  };

  return (
    <>
      {enableBreadCrumb === false ? (
        <div
          className={clsx('fas', 'fa-file-code', 'fa-file-code2', 'teamSelector')}
          style={{ marginRight: '1vw', marginTop: '0.5vw', transform: 'translate(-50%, 0)' }}
        >
          {' '}
        </div>
      ) : (
        <div
          data-tooltip="Export project code"
          data-tooltip-pos="bottom"
          className={clsx('fas', 'fa-file-code', 'teamSelector')}
          style={{ marginRight: '1vw', marginTop: '0.5vw' }}
          onClick={(event) => openDisplayExportOptions(event)}
          aria-hidden="true"
        >
          {' '}
        </div>
      )}
      {stateDownloadptions === false ? (
        <span>&nbsp;</span>
      ) : (
        <ClickAwayListener onClickAway={closeDisplayExportOptions}>
          <span>&nbsp;</span>
        </ClickAwayListener>
      )}
      <Menu
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        anchorEl={anchorEl2}
        keepMounted
        open={stateDownloadptions}
        MenuListProps={{ onMouseLeave: closeDisplayExportOptions }}
      >
        {downloadOptions.map((option, index) => (
          <MyMenuItem key={option} onClick={() => selectExportOption(index)}>
            {option}
          </MyMenuItem>
        ))}
      </Menu>
      <ExportProjectCodeViolationsPopUp
        open={openExportProjectCodePopUp}
        setOpen={setOpenExportProjectCodePopUp}
        title={violationMessage}
      />
    </>
  );
};

export default ExportProjectCode;
export { createJavascriptPerPage };
